import React, { useState } from "react";

const Step1Illustration = () => (
  <div className="relative">
    <svg className="w-32 h-32" viewBox="0 0 200 200">
      <defs>
        <linearGradient id="passionGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ff6b6b" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#ffa726" stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="skillsGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4ecdc4" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#45b7d1" stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="painGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#9c88ff" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#667eea" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      <circle
        cx="90"
        cy="90"
        r="40"
        fill="url(#passionGrad)"
        className="animate-pulse"
      />
      <circle
        cx="110"
        cy="90"
        r="40"
        fill="url(#skillsGrad)"
        className="animate-pulse"
        style={{ animationDelay: "0.5s" }}
      />
      <circle
        cx="100"
        cy="110"
        r="40"
        fill="url(#painGrad)"
        className="animate-pulse"
        style={{ animationDelay: "1s" }}
      />
      <text x="75" y="85" fontSize="10" fontWeight="600" fill="#2d3748">
        Passion
      </text>
      <text x="115" y="85" fontSize="10" fontWeight="600" fill="#2d3748">
        Skills
      </text>
      <text x="85" y="135" fontSize="10" fontWeight="600" fill="#2d3748">
        Market
      </text>
      <text x="85" y="105" fontSize="12" fontWeight="bold" fill="#1a202c">
        Sweet Spot
      </text>
    </svg>
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-yellow-400">
      ⭐
    </div>
  </div>
);

const steps = [
  {
    title: "DISCOVER Your Profitable Sweet Spot",
    phase: "Foundation Phase",
    subtitle: "What to Sell",
    core: "Find the intersection of your passion, skills, and market demand through systematic market research and personal inventory.",
    actions: [
      "Conduct your personal inventory audit (skills, interests, experiences)",
      "Validate your business idea using the MVP approach",
      "Identify your ideal customer's pain points",
      "Create your irresistible offer using value stacking principles",
    ],
    outcome:
      "A validated, profitable business idea with a clear target market and compelling offer.",
    Illustration: <Step1Illustration />,
    color: "from-red-400 to-orange-400",
    bgColor: "bg-red-50",
    icon: "🎯",
    letter: "D",
  },
  {
    title: "BUILD Your Authority Platform",
    phase: "Attraction Phase",
    subtitle: "Visibility & Trust",
    core: "Establish yourself as the go-to expert in your niche by creating valuable content and building your digital presence across the right channels.",
    actions: [
      "Choose the optimal platforms for your specific audience",
      "Implement the authority-building content framework",
      "Create high-converting lead magnets that build your email list",
      "Master platform-specific strategies for maximum organic reach",
    ],
    outcome:
      "A growing audience of engaged prospects who see you as a trusted authority in your field.",
    Illustration: (
      <div className="w-20 h-20 text-6xl text-blue-500 animate-bounce">👥</div>
    ),
    color: "from-blue-400 to-cyan-400",
    bgColor: "bg-blue-50",
    icon: "👥",
    letter: "E",
  },
  {
    title: "CONVERT Through Automated Systems",
    phase: "Sales Phase",
    subtitle: "Turn Traffic into Revenue",
    core: "Build automated sales systems that convert your audience into paying customers using psychology-driven sequences and optimized sales processes.",
    actions: [
      "Create email sequences that nurture and sell automatically",
      "Build high-converting sales pages using proven formulas",
      "Master the digital customer journey and trust-building process",
      "Implement webinar systems for higher-ticket sales",
    ],
    outcome:
      "Predictable revenue through automated systems that sell while you sleep.",
    Illustration: (
      <div
        className="w-20 h-20 text-6xl text-green-500 animate-spin"
        style={{ animationDuration: "4s" }}
      >
        ⚙️
      </div>
    ),
    color: "from-green-400 to-emerald-400",
    bgColor: "bg-green-50",
    icon: "⚙️",
    letter: "A",
  },
  {
    title: "SCALE Your Reach and Revenue",
    phase: "Growth Phase",
    subtitle: "Expansion and Optimization",
    core: "Expand your market presence through strategic growth initiatives and team-building for long-term sustainability.",
    actions: [
      "Execute strategic launches that create market buzz",
      "Master paid traffic across Facebook, Google, and YouTube",
      "Build systems and hire team members for sustainable growth",
      "Develop multiple revenue streams for stability",
    ],
    outcome:
      "A scalable business that grows beyond your personal time investment with predictable, increasing revenue.",
    Illustration: (
      <div className="w-20 h-20 text-6xl text-purple-500 animate-pulse">📈</div>
    ),
    color: "from-purple-400 to-pink-400",
    bgColor: "bg-purple-50",
    icon: "📈",
    letter: "L",
  },
  {
    title: "OPTIMIZE for Maximum Performance",
    phase: "Data Phase",
    subtitle: "Measure and Improve",
    core: "Use data-driven optimization to maximize every aspect of your business performance, from conversion rates to customer lifetime value.",
    actions: [
      "Track the KPIs that actually matter for your business",
      "Implement systematic A/B testing across your entire funnel",
      "Optimize customer lifetime value through retention and upselling",
      "Apply the 80/20 rule to focus on highest-impact improvements",
    ],
    outcome:
      "A fine-tuned business machine that consistently improves performance and profitability.",
    Illustration: (
      <div
        className="w-20 h-20 text-6xl text-red-500 animate-pulse"
        style={{ animationDelay: "0.5s" }}
      >
        ⏰
      </div>
    ),
    color: "from-red-400 to-rose-400",
    bgColor: "bg-red-50",
    icon: "⏰",
    letter: "T",
  },
  {
    title: "SUSTAIN Long-term Success",
    phase: "Legacy Phase",
    subtitle: "Build for the Future",
    core: "Develop the mindset, systems, and strategies needed for long-term success, wealth preservation, and meaningful impact.",
    actions: [
      "Master entrepreneur-specific time management and productivity systems",
      "Build resilience and continuous learning habits",
      "Implement proper financial management and wealth-building strategies",
      "Create systems that allow your business to thrive beyond your daily involvement",
    ],
    outcome:
      "True financial freedom with a business that creates lasting wealth and positive impact.",
    Illustration: (
      <div
        className="w-20 h-20 text-6xl text-slate-600 animate-pulse"
        style={{ animationDelay: "1s" }}
      >
        🛡️
      </div>
    ),
    color: "from-slate-400 to-gray-400",
    bgColor: "bg-slate-50",
    icon: "🛡️",
    letter: "H",
  },
];

const WealthMethodOutline = () => {
  const [activeStep, setActiveStep] = useState(0);

  const currentStep = steps[activeStep];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Sidebar - Step Navigation - Fixed and Smaller */}
      <div className="w-64 bg-white shadow-xl border-r border-gray-200 fixed left-0 top-0 h-full overflow-y-auto z-10">
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
          <h1 className="text-xl font-bold mb-1">W.E.A.L.T.H.</h1>
          <p className="text-xs opacity-90">Your Path to Digital Freedom</p>
        </div>

        {/* Step Cards */}
        <div className="p-3 space-y-2">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`w-full text-left p-3 rounded-lg border-2 transition-all duration-200 hover:shadow-sm ${
                activeStep === index
                  ? `${step.bgColor} border-gray-300 shadow-sm`
                  : "bg-gray-50 border-gray-200 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center mb-2">
                <div
                  className={`w-6 h-6 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white font-bold text-xs mr-2`}
                >
                  {step.letter}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    Step {index + 1}
                  </div>
                  <div className="font-medium text-xs text-gray-700 truncate">
                    {step.phase}
                  </div>
                </div>
                {activeStep === index && (
                  <div className="text-gray-400 text-sm">→</div>
                )}
              </div>
              <div className="text-xs text-gray-600 leading-tight">
                {step.title.split(" ")[0]} - {step.subtitle}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area - Offset by sidebar width */}
      <div className="flex-1 ml-64 bg-white shadow-inner">
        <div className="h-screen overflow-y-auto">
          <div className="max-w-6xl mx-auto p-8">
            {/* Step Header */}
            <div className="text-center mb-12">
              <div
                className={`inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r ${currentStep.color} text-white font-semibold mb-4 shadow-lg`}
              >
                <span className="mr-2 text-lg">{currentStep.icon}</span>
                {currentStep.phase}
              </div>
              <h1 className="text-5xl font-bold text-gray-800 mb-4">
                {currentStep.title}
              </h1>
              <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
                {currentStep.subtitle}
              </p>
            </div>

            {/* Core Description and Illustration */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 mb-12 shadow-lg border border-gray-100">
              <div className="grid lg:grid-cols-3 gap-8 items-center">
                {/* Illustration */}
                <div className="flex justify-center">
                  <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
                    {currentStep.Illustration}
                  </div>
                </div>

                {/* Core Description */}
                <div className="lg:col-span-2">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4">
                    What You'll Master
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {currentStep.core}
                  </p>
                </div>
              </div>
            </div>

            {/* Key Actions Section */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center flex items-center justify-center">
                <span className="mr-3 text-yellow-500 text-4xl">✨</span>
                Key Actions in This Phase
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {currentStep.actions.map((action, i) => (
                  <div
                    key={i}
                    className="group bg-white p-6 rounded-xl border-2 border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-200"
                  >
                    <div className="flex items-start space-x-4">
                      <div
                        className={`w-8 h-8 rounded-full bg-gradient-to-r ${currentStep.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md group-hover:scale-110 transition-transform duration-200`}
                      >
                        {i + 1}
                      </div>
                      <span className="text-gray-700 leading-relaxed font-medium">
                        {action}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Outcome Section */}
            <div
              className={`bg-gradient-to-r ${currentStep.color} rounded-2xl p-8 text-white shadow-xl mb-12`}
            >
              <h2 className="text-3xl font-bold mb-4 flex items-center justify-center">
                <span className="mr-3 text-4xl">🎯</span>
                Your Expected Outcome
              </h2>
              <p className="text-xl leading-relaxed opacity-95 text-center max-w-4xl mx-auto">
                {currentStep.outcome}
              </p>
            </div>

            {/* Navigation Footer */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <div className="text-sm text-gray-500 font-medium">
                Step {activeStep + 1} of {steps.length}
              </div>
              <div className="flex space-x-3">
                {activeStep > 0 && (
                  <button
                    onClick={() => setActiveStep(activeStep - 1)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    ← Previous
                  </button>
                )}
                {activeStep < steps.length - 1 && (
                  <button
                    onClick={() => setActiveStep(activeStep + 1)}
                    className={`px-6 py-3 bg-gradient-to-r ${currentStep.color} text-white rounded-lg hover:opacity-90 transition-opacity font-medium shadow-lg`}
                  >
                    Next Step →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WealthMethodOutline;
